import { createContext, useState, useContext, useEffect, useCallback } from "react";
import api from "../api/axios";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartLoading, setCartLoading] = useState(false);

  const isLoggedIn = () => !!localStorage.getItem("token");

  const mapItems = (backendItems) => {
    if (!Array.isArray(backendItems)) return [];
    return backendItems.map((item) => ({
      cartItemId: item.id,
      id:         item.product,
      name:       item.product_name,
      price:      item.product_price,
      quantity:   item.quantity,
      category:   item.product_category || "",
      image:      item.product_image || null,
    }));
  };

  const extractItems = (data) => {
    if (!data) return [];
    if (Array.isArray(data))          return data;
    if (Array.isArray(data.items))    return data.items;
    if (data.cart?.items)             return data.cart.items;
    return [];
  };

  const fetchCart = useCallback(async () => {
    if (!isLoggedIn()) { setCartItems([]); return; }
    setCartLoading(true);
    try {
      const res = await api.get("cart/");
      setCartItems(mapItems(extractItems(res.data)));
    } catch (err) {
      console.error("Cart fetch error:", err);
    } finally {
      setCartLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // ── Add to cart — accepts full product object + optional quantity ──────────
  const addToCart = async (product, quantity = 1) => {
    if (!isLoggedIn()) return;
    try {
      const res = await api.post("cart/add/", {
        product_id: product.id,
        quantity,
      });
      const items = extractItems(res.data);
      if (items.length > 0) {
        setCartItems(mapItems(items));
      } else {
        await fetchCart();
      }
    } catch (err) {
      console.error("Add to cart error:", err);
      await fetchCart();
    }
  };

  const removeFromCart = async (productId) => {
    const item = cartItems.find((i) => i.id === productId);
    if (!item) return;
    try {
      const res = await api.delete("cart/remove/", {
        data: { item_id: item.cartItemId },
      });
      const items = extractItems(res.data);
      if (items.length > 0 || res.data?.items?.length === 0) {
        setCartItems(mapItems(items));
      } else {
        await fetchCart();
      }
    } catch (err) {
      console.error("Remove from cart error:", err);
      await fetchCart();
    }
  };

  const updateQuantity = async (productId, quantity) => {
    const item = cartItems.find((i) => i.id === productId);
    if (!item) return;
    try {
      const res = await api.patch("cart/update/", {
        item_id: item.cartItemId,
        quantity,
      });
      const items = extractItems(res.data);
      if (items.length > 0) {
        setCartItems(mapItems(items));
      } else {
        await fetchCart();
      }
    } catch (err) {
      console.error("Update quantity error:", err);
      await fetchCart();
    }
  };

  const clearCart = () => setCartItems([]);

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cartItems, cartCount, cartLoading,
      addToCart, removeFromCart, updateQuantity, clearCart, fetchCart,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);